import { Button, Divider, Grid, IconButton, Skeleton, Typography } from '@mui/material';
import { Box, Stack } from '@mui/system';
import { format } from 'date-fns';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { maskSSNForDisplay } from 'src/utils/ssn-mask';
import { paths } from 'src/paths';
import { Permissions, useScopeChecker } from 'src/rbac';
import { CloseIcon, PencilIcon } from './EocDetailsDrawer';
import { formatContactNumber } from './patient-info';
import { formatPatientData } from 'src/layouts/components/utils-method';

type Props = {
  handleClosePatientInfoDrawer: any;
  patientId: any;
};

const PatientInfoDrawer = (props: Props) => {
  const { handleClosePatientInfoDrawer, patientId } = props;
  const has = useScopeChecker();
  const navigate = useNavigate();
  const { patients, editPatient, patientDetails, episodeOfCare } = paths;
  const patientData = useSelector((state: any) => state?.getSinglePatientReducer?.patient);
  const patientDob = patientData?.dateOfBirth && patientData?.dateOfBirth?.split('T')?.at(0);
  const patientDataLoader = useSelector((state: any) => state.getSinglePatientReducer);
  const getAllPatientPriorChargesData = useSelector((state: any) => state?.getAllPatientPriorChargesReducer);

  const displayStartandToDateWithoutTimeZone = (date: Date) => {
    return new Date(
      new Date(date).valueOf() +
      new Date(date).getTimezoneOffset() * 60 * 1000
    )
  };

  return (
    <Grid
      flexDirection={'column'}
      justifyContent={'space-between'}
      container
      sx={{ height: '100%' }}
    >
      <>
        <Grid xs={12}>
          <Grid
            container
            sx={{ width: '100%' }}
          >
            <Grid
              py={2}
              px={2}
              xs={12}
              sx={{ width: '100%' }}
            >
              {' '}
              <Stack
                direction="row"
                alignItems={'center'}
                justifyContent={'space-between'}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="h5">Patient Details </Typography>
                </Box>
                <IconButton onClick={handleClosePatientInfoDrawer}>
                  <CloseIcon />
                </IconButton>
              </Stack>
              <Divider sx={{ mt: 2.5 }} />
              <Stack p={2}>
                <Stack
                  flexDirection="row"
                  justifyContent="space-between"
                  alignItems={'center'}
                >
                  <Typography
                    variant="h6"
                    sx={{ color: '#4338CA' }}
                  >
                    Personal Info
                  </Typography>
                  <Box>
                    <Button
                      variant="text"
                      startIcon={<PencilIcon />}
                      onClick={(event) =>
                        navigate(`${patients}${editPatient}`, {
                          state: {
                            patientId,
                            patientData,
                            returnTo: {
                              path: `${patients}/${patientDetails}${episodeOfCare}`,
                              label: 'Back to Episode of Care',
                              state: { patientId },
                            },
                          },
                        })
                      }
                      color="inherit"
                      disabled={!has(Permissions.UPDATE_PATIENT)}
                    >
                      Edit
                    </Button>
                  </Box>
                </Stack>

                <Box>
                  {patientDataLoader?.loading ? (
                    <Box mt={1}>
                      {' '}
                      {['1', '2', '3'].map((sle) => {
                        return (
                          <Box
                            sx={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}
                            key={sle}
                          >
                            <Typography
                              width={'20%'}
                              variant="h1"
                            >
                              <Skeleton animation={false} />
                            </Typography>
                            <Typography
                              width={'75%'}
                              variant="h1"
                            >
                              <Skeleton animation={false} />
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  ) : (
                    <Box>
                      <Stack>
                        <Typography
                          sx={{ color: '#6C737F' }}
                          variant="overline"
                        >
                          Basic Details
                        </Typography>
                        <Box mt={2}>
                          <Stack
                            flexDirection="row"
                            justifyContent="space-between"
                            py={1}
                          >
                            <Stack>
                              <Typography
                                noWrap
                                variant="subtitle2"
                              >
                                Activation Status
                              </Typography>
                            </Stack>
                            <Stack sx={{ width: '50%' }}>
                              <Typography
                                variant="body2"
                                sx={{ color: '#6C737F' }}
                                noWrap
                              >
                                {patientData?.active ? 'Active' : 'Inactive'}
                              </Typography>
                            </Stack>
                          </Stack>

                          <Stack
                            flexDirection="row"
                            justifyContent="space-between"
                            py={1}
                          >
                            <Stack>
                              <Typography variant="subtitle2">Gender</Typography>
                            </Stack>
                            <Stack sx={{ width: '50%' }}>
                              <Typography
                                color="text.secondary"
                                sx={{
                                  textTransform: 'capitalize',
                                }}
                                variant="body2"
                              >
                                {patientData?.gender ? patientData?.gender?.toLowerCase() : '-'}
                              </Typography>
                            </Stack>
                          </Stack>

                          <Stack
                            flexDirection="row"
                            justifyContent="space-between"
                            py={1}
                          >
                            <Stack>
                              <Typography variant="subtitle2">Date Of Birth</Typography>
                            </Stack>
                            <Stack sx={{ width: '50%' }}>
                              <Typography
                                variant="body2"
                                sx={{ color: '#6C737F' }}
                              >
                                {patientData?.dateOfBirth
                                  ? format(
                                      new Date(
                                        new Date(patientDob).valueOf() +
                                          new Date(patientDob).getTimezoneOffset() * 60 * 1000
                                      ),
                                      'MMM dd, yyyy'
                                    )
                                  : '-'}
                              </Typography>
                            </Stack>
                          </Stack>

                          <Stack
                            flexDirection="row"
                            justifyContent="space-between"
                            py={1}
                          >
                            <Stack>
                              <Typography variant="subtitle2">Phone Number</Typography>
                            </Stack>
                            <Stack sx={{ width: '50%' }}>
                              <Typography
                                variant="body2"
                                sx={{ color: '#6C737F' }}
                              >
                                {patientData?.patientPhoneNo
                                  ? formatContactNumber(patientData?.patientPhoneNo).formattedValue
                                  : '-'}
                              </Typography>
                            </Stack>
                          </Stack>

                          <Stack
                            flexDirection="row"
                            justifyContent="space-between"
                            py={1}
                          >
                            <Stack>
                              <Typography variant="subtitle2">Email</Typography>
                            </Stack>
                            <Stack sx={{ width: '50%' }}>
                              <Typography
                                variant="body2"
                                sx={{ color: '#6C737F' }}
                              >
                                {patientData?.patientEmail ? patientData?.patientEmail : '-'}
                              </Typography>
                            </Stack>
                          </Stack>
                        </Box>
                      </Stack>
                      <Divider sx={{ mt: 2 }} />
                      <Stack mt={2}>
                        <Typography
                          sx={{ color: '#6C737F' }}
                          variant="overline"
                        >
                          Therapy Details
                        </Typography>
                        <Box mt={2}>
                          <Stack
                            flexDirection="row"
                            justifyContent="space-between"
                            py={1}
                          >
                            <Stack>
                              <Typography variant="subtitle2">Primary Physician</Typography>
                            </Stack>
                            <Stack sx={{ width: '50%' }}>
                              <Typography
                                variant="body2"
                                sx={{ color: '#6C737F' }}
                                noWrap
                              >
                                {patientData?.physicianEntitySet &&
                                patientData?.physicianEntitySet?.length !== 0
                                  ? patientData?.physicianEntitySet
                                      ?.map((physician: any) => physician.physicianName)
                                      .join('')
                                  : '-'}
                              </Typography>
                            </Stack>
                          </Stack>

                          <Stack
                            flexDirection="row"
                            justifyContent="space-between"
                            py={1}
                          >
                            <Stack>
                              <Typography variant="subtitle2">Medical Record Number</Typography>
                            </Stack>
                            <Stack sx={{ width: '50%' }}>
                              <Typography
                                variant="body2"
                                sx={{ color: '#6C737F' }}
                                noWrap
                              >
                                {patientData?.medicalRecordNumber
                                  ? patientData?.medicalRecordNumber
                                  : '-'}
                              </Typography>
                            </Stack>
                          </Stack>

                          <Stack
                            flexDirection="row"
                            justifyContent="space-between"
                            py={1}
                          >
                            <Stack>
                              <Typography variant="subtitle2">Social Security Number</Typography>
                            </Stack>
                            <Stack sx={{ width: '50%' }}>
                              <Typography
                                variant="body2"
                                sx={{ color: '#6C737F' }}
                              >
                                {patientData?.socialSecurityNumber
                                  ? maskSSNForDisplay(patientData?.socialSecurityNumber?.toString())
                                  : '-'}
                              </Typography>
                            </Stack>
                          </Stack>
                          <Stack
                            flexDirection="row"
                            justifyContent="space-between"
                            py={1}
                          >
                            <Stack>
                              <Typography variant="subtitle2">Bed Assignment</Typography>
                            </Stack>
                            <Stack sx={{ width: '50%' }}>
                              <Typography
                                variant="body2"
                                sx={{ color: '#6C737F' }}
                              >
                                {formatPatientData(
                                  patientData?.floorNumber,
                                  patientData?.roomNumber,
                                  patientData?.bedAssignment
                                )}
                              </Typography>
                            </Stack>
                          </Stack>
                        </Box>
                      </Stack>
                      <Divider sx={{ mt: 2 }} />
                      <Stack mt={2}>
                        <Typography
                          sx={{ color: '#6C737F' }}
                          variant="overline"
                        >
                          Prior Medicare B Charges
                        </Typography>
                        <Box mt={2}>
                          {(() => {
                            // Priority 1: Use API data from getAllPatientPriorChargesData (if available and no error)
                            if (
                              getAllPatientPriorChargesData &&
                              !getAllPatientPriorChargesData.isError &&
                              getAllPatientPriorChargesData.priorCharges &&
                              Array.isArray(getAllPatientPriorChargesData.priorCharges) &&
                              getAllPatientPriorChargesData.priorCharges.length > 0
                            ) {
                              return getAllPatientPriorChargesData.priorCharges.map((charge: any, index: number) => {
                                const fromDate = charge.fromDate ? format(
                                  displayStartandToDateWithoutTimeZone(charge.fromDate),
                                  'MM/dd/yyyy'
                                ) : null;
                                const toDate = charge.toDate ? format(
                                  displayStartandToDateWithoutTimeZone(charge.toDate),
                                  'MM/dd/yyyy'
                                ) : null;
                                const year = charge.year || '-';
                                const dateRange = fromDate && toDate ? `${fromDate} - ${toDate}` : year;

                                return (
                                  <Box key={charge.id || `api-charge-${index}`} sx={{ mb: index < getAllPatientPriorChargesData.priorCharges.length - 1 ? 2 : 0 }}>
                                    <Typography
                                      variant="subtitle2"
                                      sx={{ mb: 1 }}
                                    >
                                      {dateRange}:
                                    </Typography>
                                    <Stack
                                      flexDirection="row"
                                      justifyContent="space-between"
                                      py={0.5}
                                    >
                                      <Stack>
                                        <Typography variant="body2">PT/SLP</Typography>
                                      </Stack>
                                      <Stack sx={{ width: '50%' }}>
                                        <Typography
                                          variant="body2"
                                          sx={{ color: '#6C737F' }}
                                          noWrap
                                        >
                                          ${charge.ptSlpCharges !== undefined && charge.ptSlpCharges !== null ? charge.ptSlpCharges : '-'}
                                        </Typography>
                                      </Stack>
                                    </Stack>
                                    <Stack
                                      flexDirection="row"
                                      justifyContent="space-between"
                                      py={0.5}
                                    >
                                      <Stack>
                                        <Typography variant="body2">OT</Typography>
                                      </Stack>
                                      <Stack sx={{ width: '50%' }}>
                                        <Typography
                                          variant="body2"
                                          sx={{ color: '#6C737F' }}
                                          noWrap
                                        >
                                          ${charge.otCharges !== undefined && charge.otCharges !== null ? charge.otCharges : '-'}
                                        </Typography>
                                      </Stack>
                                    </Stack>
                                  </Box>
                                );
                              });
                            }

                            // Priority 2: Check if patientData has new format (priorCharges array)
                            const priorCharges = patientData?.priorCharges;
                            if (priorCharges && Array.isArray(priorCharges) && priorCharges.length > 0) {
                              return priorCharges.map((charge: any, index: number) => {
                                const fromDate = charge.fromDate ? format(
                                  displayStartandToDateWithoutTimeZone(charge.fromDate),
                                  'MM/dd/yyyy'
                                ) : null;
                                const toDate = charge.toDate ? format(
                                  displayStartandToDateWithoutTimeZone(charge.toDate),
                                  'MM/dd/yyyy'
                                ) : null;
                                const year = charge.year || '-';
                                const dateRange = fromDate && toDate ? `${fromDate} - ${toDate}` : year;

                                return (
                                  <Box key={charge.id || `patient-charge-${index}`} sx={{ mb: index < priorCharges.length - 1 ? 2 : 0 }}>
                                    <Typography
                                      variant="subtitle2"
                                      sx={{ mb: 1 }}
                                    >
                                      {dateRange}:
                                    </Typography>
                                    <Stack
                                      flexDirection="row"
                                      justifyContent="space-between"
                                      py={0.5}
                                    >
                                      <Stack>
                                        <Typography variant="body2">PT/SLP</Typography>
                                      </Stack>
                                      <Stack sx={{ width: '50%' }}>
                                        <Typography
                                          variant="body2"
                                          sx={{ color: '#6C737F' }}
                                          noWrap
                                        >
                                          ${charge.ptSlpCharges !== undefined && charge.ptSlpCharges !== null ? charge.ptSlpCharges : '-'}
                                        </Typography>
                                      </Stack>
                                    </Stack>
                                    <Stack
                                      flexDirection="row"
                                      justifyContent="space-between"
                                      py={0.5}
                                    >
                                      <Stack>
                                        <Typography variant="body2">OT</Typography>
                                      </Stack>
                                      <Stack sx={{ width: '50%' }}>
                                        <Typography
                                          variant="body2"
                                          sx={{ color: '#6C737F' }}
                                          noWrap
                                        >
                                          ${charge.otCharges !== undefined && charge.otCharges !== null ? charge.otCharges : '-'}
                                        </Typography>
                                      </Stack>
                                    </Stack>
                                  </Box>
                                );
                              });
                            }

                            // Priority 3: Fallback to old format for backward compatibility
                            const hasOldCharges = 
                              (patientData?.ptSlpCharges !== undefined && patientData?.ptSlpCharges !== null && patientData?.ptSlpCharges !== '') ||
                              (patientData?.otCharges !== undefined && patientData?.otCharges !== null && patientData?.otCharges !== '');
                            
                            if (hasOldCharges) {
                              return (
                                <>
                                  <Typography
                                    variant="subtitle2"
                                    sx={{ mb: 1 }}
                                  >
                                     {new Date().getFullYear()}:
                                  </Typography>
                                  <Stack
                                    flexDirection="row"
                                    justifyContent="space-between"
                                    py={0.5}
                                  >
                                    <Stack>
                                      <Typography variant="body2">PT/SLP</Typography>
                                    </Stack>
                                    <Stack sx={{ width: '50%' }}>
                                      <Typography
                                        variant="body2"
                                        sx={{ color: '#6C737F' }}
                                        noWrap
                                      >
                                        ${patientData?.ptSlpCharges !== undefined && patientData?.ptSlpCharges !== null
                                          ? patientData?.ptSlpCharges
                                          : '-'}
                                      </Typography>
                                    </Stack>
                                  </Stack>
                                  <Stack
                                    flexDirection="row"
                                    justifyContent="space-between"
                                    py={0.5}
                                  >
                                    <Stack>
                                      <Typography variant="body2">OT</Typography>
                                    </Stack>
                                    <Stack sx={{ width: '50%' }}>
                                      <Typography
                                        variant="body2"
                                        sx={{ color: '#6C737F' }}
                                        noWrap
                                      >
                                        ${patientData?.otCharges !== undefined && patientData?.otCharges !== null
                                          ? patientData?.otCharges
                                          : '-'}
                                      </Typography>
                                    </Stack>
                                  </Stack>
                                </>
                              );
                            }

                            // Show loading state if API is loading
                            if (getAllPatientPriorChargesData?.loading) {
                              return (
                                <Typography
                                  variant="body2"
                                  sx={{ color: '#6C737F' }}
                                >
                                  Loading prior charges...
                                </Typography>
                              );
                            }

                            // Show error state if API has error
                            if (getAllPatientPriorChargesData?.isError) {
                              return (
                                <Typography
                                  variant="body2"
                                  sx={{ color: '#d32f2f' }}
                                >
                                  Error loading prior charges
                                </Typography>
                              );
                            }

                            // No charges found
                            return (
                              <Typography
                                variant="body2"
                                sx={{ color: '#6C737F' }}
                              >
                                No prior charges
                              </Typography>
                            );
                          })()}
                        </Box>
                      </Stack>
                    </Box>
                  )}
                </Box>
              </Stack>
              <Divider sx={{ my: 1 }} />
            </Grid>
          </Grid>
        </Grid>
      </>
    </Grid>
  );
};

export default PatientInfoDrawer;
